'use client';

import { c as _c } from "react/compiler-runtime";
import type { User } from '@auth0/auth0-react';
import { Auth0Provider } from '@auth0/auth0-react';
import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { clearLastActiveCookie } from '@libs/core';
import { useDecisions } from '../..';
import { clearAuth0FlowNavigation, confirmAuth0AutoshipEnrollCallback, getAuth0RedirectOrigin, persistAuth0ReturnTo, readAuth0ReturnTo, sanitizeSegmentUrl } from '../../utils';
import type { Auth0AppState } from '../../utils/getAuth0LoginRedirectOptions';
import { Auth0TokenSync } from './Auth0TokenSync';
import { getAuth0SpaClientOptions } from './auth0ClientOptions';
import { clearAuth0CallbackSearch, setPendingAuth0ReturnTo } from './pendingAuth0ReturnTo';
import { resolveAuth0Destination } from './resolveAuth0Destination';
type Auth0AppProviderProps = {
  readonly children: ReactNode;
};

/**
 * Wraps the app in `Auth0Provider` when `auth0` is on.
 * Otherwise renders children unchanged so the legacy auth flow is preserved.
 *
 * Uses a single fixed callback URL (`/auth/callback`) registered in the Auth0
 * dashboard. The originating page is stored in `appState.returnTo` so the
 * callback handler can redirect the user back after login.
 *
 * `onRedirectCallback` persists `returnTo` for cart merge (keeping a value
 * already stored at login, unless this is a new registration whose destination
 * was resolved to About You), stashes it for the callback redirect, and strips
 * OAuth `code`/`state` from the URL. `Auth0TokenSync` writes the `accessToken`
 * cookie, waits for guest-cart merge on the callback route, then
 * document-navigates so cookie-gated routes (Treats, account) do not race
 * the first landing.
 *
 * Mounted from the shared `PageLayout` so all routes that go through it
 * (ACP, sign-in, cart, etc.) get a single Auth0 session context.
 */
export function Auth0AppProvider(t0) {
  const $ = _c(6);
  const {
    children
  } = t0;
  const isAuth0Enabled = useDecisions("auth0");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getAuth0RedirectOrigin();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const redirectOrigin = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (appState, user) => {
      clearLastActiveCookie();
      clearAuth0FlowNavigation();
      confirmAuth0AutoshipEnrollCallback(appState?.isInStoreAutoship === true);
      const returnTo = sanitizeSegmentUrl(resolveAuth0Destination(appState, user), redirectOrigin, {
        includeSearch: true
      });
      const appStateReturnTo = sanitizeSegmentUrl(appState?.returnTo ?? "/", redirectOrigin, {
        includeSearch: true
      });
      const loginReturnTo = readAuth0ReturnTo();
      const mergeReturnTo = returnTo === appStateReturnTo ? loginReturnTo || returnTo : returnTo;
      persistAuth0ReturnTo(mergeReturnTo);
      setPendingAuth0ReturnTo(returnTo);
      clearAuth0CallbackSearch();
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const onRedirectCallback = t2;
  if (!isAuth0Enabled) {
    return children;
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getAuth0SpaClientOptions();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Auth0TokenSync />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== children) {
    t5 = <Auth0Provider {...t3} onRedirectCallback={onRedirectCallback}>{t4}{children}</Auth0Provider>;
    $[4] = children;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}